<script setup lang="ts">
import { UiButton, UiHubCard } from '@ui'
import Avatar from '@ui/components/Avatar/Avatar.vue'
import type { HubItem } from '~~/types/types'

const { locale } = useI18n()
const localeRoute = useLocaleRoute()

definePageMeta({
  layout: 'base',
  key: (route) => route.fullPath,
})

const {
  public: { appLink },
} = useRuntimeConfig()

const { t } = useI18n()

useSeoMeta({
  title: 'All Hubs',
  description: 'Hubs page',
})

const take = 24

const queryData = computed(() => ({
  take,
  locale: locale.value,
}))

// first load
const pages = useState<{ page: number; data: HubItem[] }[]>('hubs', () => [])
const { data: firstPage, pending } =
  await useNuxtApp().$client.hubs.getHubs.useQuery(queryData)

const canLoadMore = ref(false)
const isNextPageLoading = ref(false)

watch(
  firstPage,
  (value) => {
    pages.value = []

    if (value?.data.length) {
      if (!pages.value.length) {
        pages.value.push({
          page: 1,
          data: value?.data,
        })
      }

      canLoadMore.value = value.meta.take === value.data.length
    }
  },
  {
    immediate: true,
  }
)

const onClickHandler = async () => {
  isNextPageLoading.value = true

  const page = pages.value.at(-1)?.page

  try {
    if (!page) return

    const { data, meta } = await useNuxtApp().$client.hubs.getHubs.query({
      page: page + 1,
      ...queryData.value,
    })

    if (!data.length || meta.take !== data.length) {
      throw new Error('No more data')
    }

    pages.value.push({ page: page + 1, data })
  } catch (e) {
    canLoadMore.value = false
  }

  isNextPageLoading.value = false
}
</script>

<template>
  <div class="container mx-auto">
    <section v-if="false" class="flex">
      <div>
        <!-- <Breadcrumbs class="breadcrumbs">
          <template #breadcrumb="{ to, title, first }">
            <span v-if="!first" class="px-2">›</span>
            <NuxtLink :to="localeRoute(to)">
              {{ title }}
            </NuxtLink>
          </template>
        </Breadcrumbs> -->
      </div>
      <div></div>
    </section>
    <section class="mx-3.5">
      <div
        v-if="pages.length"
        class="grid grid-flow-row gap-5 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
      >
        <template v-for="{ data } in pages">
          <div v-for="hub in data">
            <NuxtLink
              :to="localeRoute(`/hubs/${hub.slug}`)"
              class="cursor-pointer"
            >
              <UiHubCard
                :image="
                  hub.mediaData?.coverImage?.large
                    ? `${appLink}/${hub.mediaData?.coverImage?.large}`
                    : ''
                "
                :coverImageDominantColors="
                  hub.mediaData
                    ? Array.isArray(hub.mediaData.coverImageDominantColors)
                      ? hub.mediaData.coverImageDominantColors
                      : []
                    : []
                "
              >
                <template #title> {{ hub.title }} </template>
                <template v-if="hub.members" #subheader>
                  {{ hub.members }} Members
                </template>
                <template #avatar>
                  <Avatar
                    :url="
                      hub.mediaData?.avatar?.small
                        ? `${appLink}/${hub.mediaData?.avatar?.small}`
                        : ''
                    "
                  />
                </template>
                <template v-if="hub.hubType === 'nftCollection'" #badge>
                  <Icon
                    name="belong:hub-verified"
                    class="min-w-4.75 max-w-4.75 min-h-5 max-h-5 ml-2"
                  />
                </template>
              </UiHubCard>
            </NuxtLink>
          </div>
        </template>
      </div>
      <div v-else>No Hubs</div>

      <div v-if="isNextPageLoading || pending" class="w-20 mx-auto my-8">
        <Lottie src="/lottie/loading.json" autoplay loop />
      </div>
    </section>

    <section v-if="canLoadMore">
      <div class="flex justify-center mt-3.5">
        <UiButton
          @click="onClickHandler()"
          variant="gray-outline-website"
          class="max-w-100 w-full"
        >
          <p class="text-white text-center">
            {{ t('load-more') }}
          </p>
        </UiButton>
      </div>
    </section>
  </div>
</template>

<style scoped>
@reference "tailwindcss";
.breadcrumbs {
  &:deep(ul) {
    @apply flex flex-row;

    li {
      @apply text-sm;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
